import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { env } from '@/core/env/env';

import type { AuthConfig } from './auth-config';

/**
 * Sign-in `UserManager` params only — logout sends `logout_uri` instead (see `signOutAndRevoke`).
 * `'code'` enables PKCE; the deprecated implicit grant (`'token'`) must never be used.
 */
export const OIDC_RESPONSE_TYPE = 'code';

/**
 * Module-scoped state. Deliberately not a React context: route guards are loaders, not components,
 * and run before the router (and its context) mounts.
 */
let userManager: UserManager | null = null;
let hostedUiOrigin: string | null = null;
let clientId: string | null = null;

/**
 * Initializes the OIDC client with configuration fetched from the backend. Must be called exactly
 * once during boot, before any OIDC operation. Throws on second call to prevent silent pool swaps.
 */
export function initOidc(config: AuthConfig): void {
  if (userManager !== null) {
    throw new Error('initOidc() called twice — OIDC is already initialized');
  }

  const region = config.poolId.split('_')[0];
  const authority = `https://cognito-idp.${region}.amazonaws.com/${config.poolId}`;

  hostedUiOrigin = `https://${config.domain}`;
  clientId = config.clientId;

  userManager = new UserManager({
    authority,
    client_id: config.clientId,
    redirect_uri: env.VITE_COGNITO_REDIRECT_SIGN_IN,
    // Inert: Cognito's /logout rejects the id_token_hint form signoutRedirect() sends, so
    // oidc-session.ts builds the logout URL by hand. The value must still be registered as
    // an Allowed sign-out URL (not a callback URL) on the app client.
    post_logout_redirect_uri: env.VITE_COGNITO_REDIRECT_SIGN_OUT,
    response_type: OIDC_RESPONSE_TYPE,
    scope: config.scopes.join(' '),
    // localStorage, not the library's default per-tab sessionStorage: a new tab would otherwise
    // force a full Hosted UI round trip.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Refresh timing is driven by 401s in the API interceptor; a renew timer here would race it.
    automaticSilentRenew: false,
    // No check_session_iframe from Cognito; this would only add a second source of session truth.
    monitorSession: false,
    // Overrides discovery (the seed wins the merge), so a moved or custom-domain endpoint would
    // pin the wrong URL and revocation would fail on every logout while logout looked successful.
    // Re-check when the domain changes. Reasoning: docs/adr/authentication.md, Logout.
    metadataSeed: {
      revocation_endpoint: `${hostedUiOrigin}/oauth2/revoke`,
    },
    // Bounds every request the library makes; unset, it skips its AbortController entirely and has
    // no default. revokeTokens() costs two requests (discovery, then the revocation POST) on a
    // full-page navigation, so a stall leaves the session never removed and Cognito's /logout never
    // reached — the user stays signed in with nothing shown. See docs/adr/authentication.md, Logout.
    requestTimeoutInSeconds: 10,
    // Only the refresh token: an access-token revoke is rejected by Cognito and would look like
    // a spurious logout failure.
    revokeTokenTypes: ['refresh_token'],
  });
}

/** Returns the UserManager. Throws if `initOidc()` has not been called. */
export function getUserManager(): UserManager {
  if (!userManager) {
    throw new Error('initOidc() was not called — restoreSession must run before any OIDC call');
  }
  return userManager;
}

/** Returns the Hosted UI origin (e.g. `https://auth.example.com`). Throws if uninitialized. */
export function getHostedUiOrigin(): string {
  if (!hostedUiOrigin) {
    throw new Error('initOidc() was not called — restoreSession must run before any OIDC call');
  }
  return hostedUiOrigin;
}

/** Returns the Cognito app client ID. Throws if uninitialized. */
export function getClientId(): string {
  if (!clientId) {
    throw new Error('initOidc() was not called — restoreSession must run before any OIDC call');
  }
  return clientId;
}

/** Returns true if OIDC has been initialized. Used for edge cases like logout on /session-error. */
export function isOidcInitialized(): boolean {
  return userManager !== null;
}
